<template>
  <div>
    <div class="flex w-max flex-col hover-native-only:group-hover:hidden">
      <div>
        <ProductBadge
          v-if="isProductsLastChance(product)"
          :badge-label="$t('badge_labels.last_chance.last')"
          :secondary-badge-label="$t('badge_labels.last_chance.chance')"
          class="bg-black text-white"
        />
      </div>
      <div class="flex w-max flex-col-reverse">
        <div v-if="isProductSustainable(product)">
          <ProductBadge :badge-label="$t('badge_labels.sustainable')" />
        </div>
        <div v-if="product.isNew">
          <ProductBadge
            :badge-label="$t('badge_labels.new')"
            class="bg-white text-black"
          />
        </div>
        <template v-if="isActiveSalePeriod">
          <div>
            <ProductBadge
              v-for="(campaign, idx) in getSalesRelativeAmountByCategory(
                product,
                'campaign',
              )"
              :key="`campaign-${idx}`"
              class="w-max bg-[#ff6e17] uppercase"
              :badge-label="
                `-${campaign.amount.relative * 100}% ` +
                $t('badge_labels.extra')
              "
            />
          </div>
          <div>
            <template v-if="!hasSaleReplacingBadge">
              <ProductBadge
                v-for="(sale, idx) in getSalesRelativeAmountByCategory(
                  product,
                  'sale',
                )"
                :key="`sale-${idx}`"
                :badge-label="`-${Math.round(sale.amount.relative * 100)}%`"
                :class="
                  blackFridayLabels
                    ? 'bg-black w-[75px] h-[38px] text-[20px] uppercase flex justify-center items-center'
                    : 'bg-red'
                "
                class="text-white"
              />
            </template>
            <div v-else class="flex w-max flex-col items-start">
              <DynamicBadge
                v-for="(badge, idx) in activeBadges.filter(
                  (b) => b.replaceSale,
                )"
                :key="`dynamic-${badge.label}-${idx}`"
                :badge="badge"
              />
            </div>
          </div>
        </template>
      </div>
      <div class="flex w-max flex-col items-start">
        <DynamicBadge
          v-for="(badge, idx) in activeBadges.filter((b) => !b.replaceSale)"
          :key="`dynamic-${badge.label}-${idx}`"
          :badge="badge"
        />
      </div>
    </div>
    <div class="hover-native-only:group-hover:hidden">
      <ProductBadge
        v-if="isPremium(product)"
        :badge-label="$t('badge_labels.premium')"
        class="top-0 mt-0 bg-black align-top text-sm font-medium uppercase text-white md:text-[15px]"
      />
      <ProductBadge
        v-if="isMonthlyMustHave(product)"
        class="bg-white !pl-0 align-top text-sm font-medium uppercase md:!py-[3px] md:text-[15px]"
        :badge-label="$t('badge_labels.monthly_must.first')"
        :secondary-badge-label="$t('badge_labels.monthly_must.second')"
        :third-badge-label="$t('badge_labels.monthly_must.third')"
      >
        <template #badgeLabelSlot>
          <span class="bg-black px-[4px] text-2xs font-normal text-white">
            {{ $t('badge_labels.monthly_must.first') }}
          </span>
        </template>
        <template #badgeSecondaryLabelSlot>
          <div class="pl-[5px] text-xs font-bold uppercase md:text-lg">
            {{ $t('badge_labels.monthly_must.second') }}
          </div>
        </template>
        <template #badgeThirdLabelSlot>
          <div class="pl-[5px] text-xs font-bold uppercase md:text-lg">
            {{ $t('badge_labels.monthly_must.third') }}
          </div>
        </template>
      </ProductBadge>
    </div>
  </div>
</template>

<script setup lang="ts">
import {
  getAppliedReductionsByCategory,
  getFirstAttributeValue,
} from '@scayle/storefront-nuxt'
import type { Product } from '@scayle/storefront-nuxt'
import { isProductSustainable } from '#layers/base/utils/sustainability'
import { LAST_CHANCE_CATEGORY_IDS } from '#layers/base/constants/category'
import { useDynamicBadges } from '#layers/base/composables/useDynamicBadges'
import useGlobalConfig from '~/composables/useGlobalConfig'
import useSmartShopConfiguration from '#layers/base/composables/useSmartShopConfiguration'
import { getLowestPrice } from '~/utils/product'
import type { SbDynamicBadge } from '~/cms/storyblok/types/storyblok.gen'

type ProductCardBadgesProps = {
  product: Product
}

const props = defineProps<ProductCardBadgesProps>()

const { blackFridayLabels, config } = useGlobalConfig()
const { isActiveSalePeriod } = useSmartShopConfiguration()
const showDynamicBadges = ref(false)
onMounted(() => {
  requestAnimationFrame(() => {
    showDynamicBadges.value = true
  })
})
const { activeBadges } = useDynamicBadges(
  () => props.product,
  () =>
    showDynamicBadges.value
      ? (config.value?.dynamicBadges as SbDynamicBadge[]) || []
      : [],
)

const hasSaleReplacingBadge = computed(() =>
  activeBadges.value.some((badge) => badge.replaceSale),
)

const isProductsLastChance = (product: Product): boolean =>
  (product?.categories || [])
    .flat()
    .some((category) =>
      LAST_CHANCE_CATEGORY_IDS.includes(category.categoryId),
    ) || false

const getSalesRelativeAmountByCategory = (
  product: Product,
  category: 'sale' | 'campaign',
) => {
  const variantsLowestPrice =
    product.variants && getLowestPrice(product.variants)
  return variantsLowestPrice
    ? getAppliedReductionsByCategory(variantsLowestPrice, category)
    : []
}

const isPremium = (product: Product): boolean => {
  return Boolean(
    getFirstAttributeValue(product.attributes, 'premiumsegment')?.value ===
    'ja',
  )
}

const isMonthlyMustHave = (product: Product) => {
  return Boolean(
    getFirstAttributeValue(product.attributes, 'einfuehrungsgrund')?.value ===
    'monthly_must_haves',
  )
}
</script>

<style lang="postcss" scoped>
.card-container {
  @apply flex
  flex-col-reverse
  w-max;
}
.card-container--product-badge__new {
  @apply bg-white
  text-black;
}
.card-container--product-badge__last_chance {
  @apply bg-black
  text-white;
}
.card-container--product-badge__premium {
  @apply bg-black
  text-white
  top-0
  mt-0;
}
</style>
